import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardMedia, Chip, Button,
  Stack, Pagination, Alert, TextField, InputAdornment, FormControl, Select,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  CircularProgress, Avatar, IconButton, Tooltip, alpha, Rating,
} from '@mui/material';
import {
  Search, LocationOn, KingBed, People, Wifi, LocalParking, Pool,
  FitnessCenter, Security as SecurityIcon, Bolt, Water, CheckCircle,
  ArrowForward, CalendarMonth, AttachMoney, Home, FilterList,
  ImageOutlined, Close, Star, VerifiedUser,
} from '@mui/icons-material';
import { propertiesAPI, bookingsAPI, reviewsAPI } from '../../api/client';
import useAuthStore from '../../store/useStore';

// Autoplay image carousel for property/room images
function AutoplayCarousel({ images, getImageUrl, height = 200, interval = 3000, onClick }) {
  const [current, setCurrent] = React.useState(0);
  React.useEffect(() => {
    if (!images || images.length <= 1) return;
    const timer = setInterval(() => setCurrent(prev => (prev + 1) % images.length), interval);
    return () => clearInterval(timer);
  }, [images, interval]);
  if (!images || images.length === 0) return null;
  const url = getImageUrl(images[current]?.image_url);
  return (
    <Box sx={{ position: 'relative', height, overflow: 'hidden', cursor: onClick ? 'pointer' : 'default' }}
      onClick={() => onClick && onClick(current)}>
      <Box component="img" src={url} alt="" sx={{ width: '100%', height, objectFit: 'cover', transition: 'opacity 0.5s' }} />
      {images.length > 1 && (
        <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)' }}>
          {images.map((_, i) => (
            <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: i === current ? 'white' : 'rgba(255,255,255,0.5)', transition: 'background-color 0.3s' }} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';

const amenityIcons = {
  wifi: <Wifi fontSize="small" />,
  parking: <LocalParking fontSize="small" />,
  pool: <Pool fontSize="small" />,
  gym: <FitnessCenter fontSize="small" />,
  security: <SecurityIcon fontSize="small" />,
  electricity: <Bolt fontSize="small" />,
  water: <Water fontSize="small" />,
};

function BrowsePropertiesPage() {
  const { user } = useAuthStore();
  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Booking Dialog State
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [isPairing, setIsPairing] = useState(false);
  const [bookingNotes, setBookingNotes] = useState('');

  // Image viewer
  const [imageViewer, setImageViewer] = useState(null);

  // Reviews
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 12, status: 'active' };
      if (search) params.search = search;
      if (typeFilter) params.property_type = typeFilter;
      if (cityFilter) params.city = cityFilter;
      const res = await propertiesAPI.list(params);
      setProperties(res.data.properties || res.data || []);
      setTotal(res.data.total || res.data?.length || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProperties(); }, [page]);

  const handleSearch = () => { setPage(1); fetchProperties(); };

  const handlePropertySelect = async (prop) => {
    setSelectedProperty(prop);
    setSelectedRoom(null);
    setRoomsLoading(true);
    setReviewsLoading(true);
    try {
      const res = await propertiesAPI.listRooms(prop.id);
      setRooms(res.data || []);
    } catch (_) { setRooms([]); }
    finally { setRoomsLoading(false); }
    try {
      const res = await reviewsAPI.listByProperty(prop.id, { per_page: 10 });
      setReviews(res.data.reviews || []);
      setAverageRating(res.data.average_rating || 0);
      setTotalReviews(res.data.total || 0);
    } catch (_) { setReviews([]); }
    finally { setReviewsLoading(false); }
    setBookingDialog(true);
  };

  const handleBooking = async () => {
    if (!checkIn || !checkOut) {
      setError('Please select check-in and check-out dates');
      return;
    }
    setBookingLoading(true);
    try {
      await bookingsAPI.create({
        property_id: selectedProperty.id,
        check_in_date: new Date(checkIn).toISOString(),
        check_out_date: new Date(checkOut).toISOString(),
        room_number: selectedRoom?.room_number || null,
        is_paired: isPairing,
        notes: bookingNotes || null,
      });
      setSuccess('Booking submitted successfully! Check My Bookings for status.');
      setBookingDialog(false);
      setSelectedProperty(null);
      setSelectedRoom(null);
      setCheckIn('');
      setCheckOut('');
      setIsPairing(false);
      setBookingNotes('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create booking');
    }
    finally { setBookingLoading(false); }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${API_BASE}${url}`;
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Browse Properties
        </Typography>
        <Typography variant="body2" color="text.secondary">Find and book your perfect student accommodation</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Search & Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            size="small" placeholder="Search by name, location..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select value={typeFilter} displayEmpty onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
              <MenuItem value="">All Types</MenuItem>
              {['hostel', 'apartment', 'single_room', 'shared_room', 'studio'].map(t =>
                <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t.replace('_', ' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" placeholder="City" value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            sx={{ minWidth: 120 }} />
          <Button variant="contained" onClick={handleSearch} startIcon={<FilterList />}>Filter</Button>
        </Stack>
      </Paper>

      {/* Property Cards */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : properties.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <Home sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" fontWeight={600}>No properties found</Typography>
          <Typography variant="body2" color="text.secondary">Try adjusting your search filters</Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {properties.map((prop) => {
              const imgUrl = prop.images?.[0]?.image_url ? getImageUrl(prop.images[0].image_url) : null;
              const price = prop.price_per_semester || prop.price_per_year || (prop.price_per_month ? prop.price_per_month * 12 : 0);
              return (
                <Grid item xs={12} sm={6} md={4} key={prop.id}>
                  <Card sx={{ borderRadius: 3, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
                    <Box sx={{ position: 'relative', height: 200, bgcolor: 'grey.100' }}>
                      {prop.images?.length > 0 ? (
                        <AutoplayCarousel images={prop.images} getImageUrl={getImageUrl} height={200}
                          onClick={(idx) => setImageViewer({ images: prop.images, current: idx, title: prop.title })} />
                      ) : (
                        <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <ImageOutlined sx={{ fontSize: 64, color: 'grey.300' }} />
                        </Box>
                      )}
                      {prop.images?.length > 1 && (
                        <Chip label={`${prop.images.length} photos`} size="small"
                          sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', fontSize: '0.7rem', zIndex: 1 }} />
                      )}
                      <Chip label={prop.property_type?.replace('_', ' ') || 'Property'} size="small"
                        sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'white', fontWeight: 600, textTransform: 'capitalize', zIndex: 1 }} />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" fontWeight={700} noWrap gutterBottom>{prop.title}</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                        <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {prop.address}, {prop.city}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                        {prop.room_type && (
                          <Chip icon={<KingBed sx={{ fontSize: 14 }} />} label={prop.room_type?.replace('_', ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                        )}
                        {prop.available_rooms > 0 && (
                          <Chip label={`${prop.available_rooms} available`} size="small" color="success" variant="outlined" />
                        )}
                        {prop.allows_pairing && (
                          <Chip icon={<People sx={{ fontSize: 14 }} />} label="Pairing" size="small" color="info" variant="outlined" />
                        )}
                      </Stack>
                      {prop.amenities?.length > 0 && (
                        <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
                          {prop.amenities.slice(0, 4).map((a, i) => (
                            <Tooltip key={i} title={a.name || a}>
                              <Avatar sx={{ width: 24, height: 24, bgcolor: alpha('#6366f1', 0.1), color: '#6366f1' }}>
                                {amenityIcons[a.name?.toLowerCase()] || amenityIcons[a?.toLowerCase()] || <CheckCircle sx={{ fontSize: 14 }} />}
                              </Avatar>
                            </Tooltip>
                          ))}
                          {prop.amenities.length > 4 && (
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>+{prop.amenities.length - 4}</Typography>
                          )}
                        </Stack>
                      )}
                      <Box sx={{ mt: 'auto' }}>
                        <Divider sx={{ mb: 1.5 }} />
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="h6" fontWeight={800} color="primary">
                              GH₵ {price.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {prop.price_per_semester ? '/semester' : prop.price_per_year ? '/year' : '/year'}
                            </Typography>
                          </Box>
                          <Button variant="contained" size="small" endIcon={<ArrowForward />}
                            onClick={() => handlePropertySelect(prop)}
                            sx={{ borderRadius: 2, px: 2 }}>
                            Book Now
                          </Button>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {total > 12 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination count={Math.ceil(total / 12)} page={page} onChange={(e, v) => setPage(v)} color="primary" />
            </Box>
          )}
        </>
      )}

      {/* Booking Dialog with Room Selection */}
      <Dialog open={bookingDialog} onClose={() => setBookingDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Book Property</Typography>
            <Typography variant="body2" color="text.secondary">{selectedProperty?.title}</Typography>
          </Box>
          <IconButton onClick={() => setBookingDialog(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedProperty && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Property Summary */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    {selectedProperty.images?.[0]?.image_url ? (
                      <Box component="img" src={getImageUrl(selectedProperty.images[0].image_url)}
                        alt={selectedProperty.title}
                        sx={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 2 }} />
                    ) : (
                      <Box sx={{ width: '100%', height: 150, bgcolor: 'grey.200', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ImageOutlined sx={{ fontSize: 48, color: 'grey.400' }} />
                      </Box>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography variant="h6" fontWeight={700}>{selectedProperty.title}</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                      <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">{selectedProperty.address}, {selectedProperty.city}</Typography>
                    </Stack>
                    <Typography variant="h5" fontWeight={800} color="primary" sx={{ mt: 1 }}>
                      GH₵ {(selectedProperty.price_per_semester || selectedProperty.price_per_year || (selectedProperty.price_per_month || 0) * 12).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Platform fee included in total</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      {selectedProperty.available_rooms > 0 && <Chip label={`${selectedProperty.available_rooms} rooms available`} size="small" color="success" />}
                      <Chip label={selectedProperty.property_type?.replace('_', ' ')} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Room Selection */}
              {roomsLoading ? (
                <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
              ) : rooms.length > 0 ? (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>Select a Room</Typography>
                  <Grid container spacing={2}>
                    {rooms.filter(r => r.status === 'available').map((room) => (
                      <Grid item xs={12} sm={6} key={room.id}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2, borderRadius: 2, cursor: 'pointer',
                            border: selectedRoom?.id === room.id ? '2px solid' : '1px solid',
                            borderColor: selectedRoom?.id === room.id ? 'primary.main' : 'divider',
                            bgcolor: selectedRoom?.id === room.id ? alpha('#6366f1', 0.04) : 'white',
                            '&:hover': { borderColor: 'primary.light', bgcolor: alpha('#6366f1', 0.02) },
                          }}
                          onClick={() => setSelectedRoom(room)}
                        >
                          {room.images?.length > 0 && (
                            <Box sx={{ borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                              <AutoplayCarousel images={room.images} getImageUrl={getImageUrl} height={100} interval={2500} />
                            </Box>
                          )}
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography fontWeight={600}>Room {room.room_number}</Typography>
                              {room.room_label && <Typography variant="caption" color="text.secondary">{room.room_label}</Typography>}
                            </Box>
                            {selectedRoom?.id === room.id && <CheckCircle color="primary" />}
                          </Stack>
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            {room.room_type && <Chip label={room.room_type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />}
                            {room.capacity && <Chip label={`${room.capacity} person`} size="small" variant="outlined" />}
                          </Stack>
                          {(room.price_per_semester || room.price_per_month) && (
                            <Typography variant="body2" fontWeight={700} color="primary" sx={{ mt: 1 }}>
                              GH₵ {(room.price_per_semester || (room.price_per_month * 6)).toLocaleString()}
                            </Typography>
                          )}
                          {room.features && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {room.features}
                            </Typography>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                    {rooms.filter(r => r.status === 'available').length === 0 && (
                      <Grid item xs={12}>
                        <Alert severity="warning">No available rooms. You can still book the property directly.</Alert>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ) : null}

              <Divider />

              {/* Booking Details */}
              <Typography variant="subtitle1" fontWeight={700}>Booking Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Check-in Date" type="date" value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().split('T')[0] }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Check-out Date" type="date" value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: checkIn || new Date().toISOString().split('T')[0] }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Notes (optional)" multiline rows={2}
                    value={bookingNotes} onChange={(e) => setBookingNotes(e.target.value)}
                    placeholder="Any special requests or notes..." />
                </Grid>
              </Grid>

              {/* Pairing Option */}
              {selectedProperty.allows_pairing && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#6366f1', 0.02) }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography fontWeight={600}>Room Pairing</Typography>
                      <Typography variant="body2" color="text.secondary">Looking for a roommate? Enable pairing to find a match.</Typography>
                    </Box>
                    <Button variant={isPairing ? 'contained' : 'outlined'} size="small"
                      onClick={() => setIsPairing(!isPairing)}
                      startIcon={<People />}>
                      {isPairing ? 'Pairing On' : 'Enable Pairing'}
                    </Button>
                  </Stack>
                </Paper>
              )}

              {/* Reviews Section */}
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Reviews</Typography>
                  {totalReviews > 0 && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Rating value={averageRating} precision={0.1} readOnly size="small" />
                      <Typography variant="body2" fontWeight={600}>{averageRating}</Typography>
                      <Typography variant="body2" color="text.secondary">({totalReviews})</Typography>
                    </Stack>
                  )}
                </Stack>
                {reviewsLoading ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={20} /></Box>
                ) : reviews.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
                    <Typography variant="body2" color="text.secondary">No reviews yet for this property.</Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1.5}>
                    {reviews.slice(0, 5).map((rev) => (
                      <Paper key={rev.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Avatar
                            src={rev.user_avatar_url ? getImageUrl(rev.user_avatar_url) : undefined}
                            sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}
                          >
                            {(rev.user_first_name?.[0] || '?').toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="body2" fontWeight={700}>
                                  {rev.user_first_name} {rev.user_last_name}
                                </Typography>
                                {rev.is_verified && (
                                  <Tooltip title="Verified Stay">
                                    <VerifiedUser sx={{ fontSize: 14, color: 'success.main' }} />
                                  </Tooltip>
                                )}
                              </Stack>
                              <Rating value={rev.rating} precision={0.5} readOnly size="small" />
                            </Stack>
                            {rev.title && <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>{rev.title}</Typography>}
                            {rev.comment && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>{rev.comment}</Typography>}
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {new Date(rev.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                    {totalReviews > 5 && (
                      <Typography variant="body2" color="primary" sx={{ textAlign: 'center', cursor: 'pointer' }}>
                        View all {totalReviews} reviews
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>

              <Divider />

              {/* Tenant Info */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Your Information</Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">Name</Typography><Typography fontWeight={500}>{user?.first_name} {user?.last_name}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">Email</Typography><Typography fontWeight={500}>{user?.email}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">Phone</Typography><Typography fontWeight={500}>{user?.phone || 'Not set'}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">University</Typography><Typography fontWeight={500}>{user?.university || 'Not set'}</Typography></Grid>
                </Grid>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBookingDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBooking} disabled={bookingLoading || !checkIn || !checkOut}
            startIcon={bookingLoading ? <CircularProgress size={16} /> : <CalendarMonth />}
            sx={{ px: 3 }}>
            {bookingLoading ? 'Booking...' : 'Confirm Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer */}
      {imageViewer && (
        <Dialog open={true} onClose={() => setImageViewer(null)} maxWidth="lg" fullWidth
          PaperProps={{ sx: { bgcolor: 'black', borderRadius: 3 } }}>
          <DialogContent sx={{ p: 1, position: 'relative' }}>
            <IconButton onClick={() => setImageViewer(null)}
              sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
              <Close />
            </IconButton>
            <Box component="img"
              src={getImageUrl(imageViewer.images[imageViewer.current]?.image_url)}
              alt={imageViewer.title}
              sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 2 }} />
            {imageViewer.images.length > 1 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto', pb: 1 }}>
                {imageViewer.images.map((img, i) => (
                  <Box key={i} component="img"
                    src={getImageUrl(img.image_url)}
                    sx={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 1, cursor: 'pointer',
                      border: i === imageViewer.current ? '2px solid white' : '2px solid transparent',
                      opacity: i === imageViewer.current ? 1 : 0.6 }}
                    onClick={() => setImageViewer(prev => ({ ...prev, current: i }))} />
                ))}
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}

export default BrowsePropertiesPage;
